/**
 * 拼"前台地址"的公共逻辑。
 *
 * 控制台里有两处需要打开客户站点：内容页的预览（带令牌）和商品列表的直达链接。
 * 两者都要回答同一个问题——这个 host 该用 http 还是 https，所以放在一处，
 * 免得哪天本地调试的规则改了只改对一半。
 */

import type { ProductStatus } from '@icetus/contracts';

/**
 * 平台给客户站分配的默认域名后缀。
 *
 * 与官网主域 `.com` 是两件事，别合并：`.net` 在 Cloudflare 后面，负责客户站
 * 与 CF for SaaS 回源；`.com` 直连源站，只服务官网和控制台。
 */
const PLATFORM_SITE_DOMAIN = 'icetus.net';

/**
 * 预览宿主。
 *
 * 客户站的默认地址 `{slug}.icetus.net` 走 Cloudflare —— 对海外访客是加速，
 * 但从国内打开一次要 4~29 秒。控制台的内联预览是"改一句看一眼"的动作，
 * 一天几十次，必须走直连。`preview.icetus.com` 与源站同一台机器、同一条链路，
 * 且与 `console.icetus.com` **不同源**（iframe 的 sandbox 才仍然有效）。
 *
 * 为什么客户端写死域名：与控制台里其它几个正式域名链接一致 —— 控制台是独立产物，
 * 拿走单独部署时也不该指向错的地址。
 */
const PREVIEW_HOST = import.meta.env.VITE_PREVIEW_HOST ?? 'preview.icetus.com';

/**
 * 平台子域 → 预览宿主上的地址。
 *
 * 返回 null 表示"这不是平台分配的地址"：客户绑了自己的域名（`schoolmemo.cn`）时
 * 预览仍然直连客户域名，因为预览宿主的路径前缀只能表示平台 slug，表达不了
 * 任意主机名 —— 与其发明一套映射，不如让那条路径继续走原地址。
 */
export function previewHostOf(
  host: string | null | undefined,
): { origin: string; prefix: string } | null {
  if (!host) return null;
  const bare = host.replace(/:\d+$/, '').toLowerCase();
  const suffix = `.${PLATFORM_SITE_DOMAIN}`;
  if (!bare.endsWith(suffix)) return null;
  const slug = bare.slice(0, -suffix.length);
  // 与 apps/site/lib/preview-host.ts 的取值形态保持一致：只有一段合法 slug 才认
  if (!/^[a-z0-9][a-z0-9-]{0,62}$/.test(slug)) return null;
  return { origin: `https://${PREVIEW_HOST}`, prefix: `/${slug}` };
}

/**
 * 前台地址的协议。
 *
 * 生产站点全部是 https（Cloudflare 强制），只有本地调试的 localhost / *.local
 * 走 http。写死 https 的话，本地点的链接会打开一个 443 上根本不存在的服务，
 * 而错误页是浏览器的、和我们的代码毫无关系，排查时最容易被带偏。
 *
 * 判本地之前先去掉端口：本地几乎总是带端口（`localhost:3000`、
 * `schoolmemo.localhost:3000`），拿带端口的字符串去比 `localhost`
 * 永远比不中，结果就是"本地也一律 https"——这个坑原来藏在内容页的
 * 预览里，2026-10-29 加测试时才发现。
 */
export function siteOrigin(host: string): string {
  const bare = host.replace(/:\d+$/, '');
  const local =
    bare === 'localhost' ||
    bare === '[::1]' ||
    bare.startsWith('127.') ||
    bare.endsWith('.localhost') ||
    bare.endsWith('.local');
  return `${local ? 'http' : 'https'}://${host}`;
}

/**
 * 商品在前台的规范地址。
 *
 * 用的是**默认语言**的地址（不带语言前缀），因为列表页不区分语言，
 * 而 `ProductSummary.slug` 就是 products.slug 本身，即默认语言的生效值
 * （非默认语言的覆盖值只存在于 ProductDetail.translations 里）。
 *
 * 站点没绑域名时返回 null：`https://undefined/products/x` 这种地址
 * 点开只会是一片空白，比按钮不可用更让人困惑。
 */
export function frontProductUrl(host: string | null | undefined, slug: string): string | null {
  if (!host || !slug) return null;
  return `${siteOrigin(host)}/products/${slug}`;
}

/**
 * 这个商品在前台能不能打开、打不开是因为什么。
 *
 * 两种"点了没内容"要分开说，因为处理方式完全不同：
 * - **站点没绑域名**是站点级问题，得去「域名」页配一个；
 * - **草稿/归档**是商品自己的状态，前台详情查询里写死了 `status = 'active'`，
 *   所以哪怕是预览态也打不开（预览令牌只管页面草稿，不管商品）。
 *
 * 草稿一律不给地址：给出一个必然 404 的链接，比按钮灰着更让人困惑 ——
 * 客户会以为是站点坏了，而不是"这东西还没上架"。
 */
export function frontTarget(
  host: string,
  item: { slug: string; status: ProductStatus },
): { href: string | null; hint: string } {
  if (item.status !== 'active') {
    return {
      href: null,
      hint:
        item.status === 'archived'
          ? '已归档的商品不在前台显示。改回「已上架」后即可打开。'
          : '草稿不会出现在前台，上架后即可打开。',
    };
  }
  const href = frontProductUrl(host, item.slug);
  return {
    href,
    hint: href ? '' : '这个站点还没有绑定域名，前台打不开。先到「域名」页绑一个。',
  };
}
